import React, { useState } from "react";
import LexicalDiscussionEditor from "../../../../common/richTextPlugins/LexicalDiscussionEditor.lazy.jsx";

import {
  project_show_path,
  project_report_edit_report_path,
  support_form_list_requests_path,
  datagrid_show_match_basket_path
} from '~/routes';

const EVENT_TYPE_ICONS = {
  application: 'fa fa-file-text-o',
  survey: 'fa fa-check-square-o',
  report: 'fa fa-bar-chart',
  disbursement: 'fa fa-check-square',
  deadline: 'fa fa-clock-o',
  support: 'adalicon icon-support',
  cross_fund_report: 'fa fa-bar-chart',
  programme_live_discussion: 'fa fa-comments',
  project_live_discussion: 'fa fa-comments',
  match_basket: 'fa fa-handshake',
};
// programme_live_discussion / project_live_discussion wrap the icon <a> in an
// `icon-${extra_data.target_type}` class today (see LiveDiscussionReplied / LiveDiscussionInitiated
// below) - replicate that via an optional per-type wrapper-class function:
const EVENT_TYPE_ICON_WRAPPER_CLASS = {
  programme_live_discussion: (extra_data) => `icon-${extra_data.target_type}`,
  project_live_discussion: (extra_data) => `icon-${extra_data.target_type}`,
};

const ownerEventCounterRef = { current: 0 };

const decodeHTML = (message) => {
  const el = document.createElement('textarea');
  el.innerHTML = message;
  return el.value;
};

function OwnerEvent({owner = null, showProfilePics}) {
  const uniqueId = `user-icon-${ownerEventCounterRef.current++}`;

  return (owner && <>
    <div className="media-left">
      <div className="media-content avatar avatar-on">
        {showProfilePics && owner.profile_pic_url
        ? <img
            src={owner.profile_pic_url}
            alt="Owner avatar"
            onError={(e) => {
              e.target.style.display = 'none';
              document.getElementById(uniqueId).style.display = 'inline';
            }}
          />
        : <i className="fa fa-user-circle-o"/>
        }
        <i id={uniqueId} className="fa fa-user-circle-o" style={{ display: 'none' }}></i>
      </div>
    </div>
    <div className="media-body">
      <div>{owner['first_name']} {owner['last_name']}</div>
    </div>
  </>)
}

function ApplicationSubmitted({item: { data: { project, extra_data, owner = null }, project_id }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-file-text-o"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('application_submitted')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('application_submitted_content', {
                        application_name: project['project_title'],
                        application_url: project_show_path({note_id: project_id}),
                        call_identifier: extra_data['call_identifier'],
                        phase_name: extra_data['phase_name']})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function ApplicationReviewCompleted({item: { data: { project, extra_data, owner = null }, project_id }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-file-text-o"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('application_review_completed')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('application_review_completed_content', {
                        application_name: project['project_title'],
                        application_url: project_show_path({note_id: project_id}),
                        call_identifier: extra_data['call_identifier'],
                        phase_name: extra_data['phase_name']})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function ApplicationDecisionGiven({item: { data: { project, extra_data, owner = null }, project_id }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-file-text-o"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('application_decision_given')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('application_decision_given_content', {
                        application_name: project['project_title'],
                        application_url: project_show_path({ note_id: project_id, phase_id: extra_data['phase_id']}),
                        decision: extra_data['decision'],
                        call_identifier: extra_data['call_identifier'],
                        phase_name: extra_data['phase_name']})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function ApplicationDecisionsPublished({item: { data: { extra_data, owner = null } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-file-text-o"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('application_decisions_published')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('application_decisions_published_content', {
                        decisions_count: extra_data['project_ids'].length,
                        call_identifier: extra_data['call_identifier'],
                        phase_name: extra_data['phase_name']})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function SurveySubmission({item: { data: { extra_data, owner = null } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-check-square-o"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('survey_submission')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('survey_submission_content', {report_name: I18n.t(extra_data['report_name'][I18n.locale])})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function NewMatchBasketMatch({item: { data: { extra_data, owner = null } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-handshake"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('new_matches')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('match_basket_new_cross_tenant_matches', {
                    basket_name: I18n.t(extra_data['match_basket_name']),
                    link: datagrid_show_match_basket_path({
                        match_basket_id: extra_data['match_basket_id'], id: extra_data['datagrid_id'], _options: true
                    })
                })}}>
            </div>
        </div>
    </>)
}

function SurveyReviewCompleted({item: { data: { extra_data, owner = null } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-check-square-o"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('survey_review_completed')}</h4>
            <div className="mb-1">
                {I18n.t(extra_data['decision'])} <span>{I18n.t(extra_data['report_name'][I18n.locale])}</span>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function SurveyDecisionGiven({item: { data: { extra_data, owner = null } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-check-square-o"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('survey_decision_given')}</h4>
            <div className="mb-1">
                {I18n.t(extra_data['decision'])} <span>{I18n.t(extra_data['report_name'][I18n.locale])}</span>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function SurveyReviewReverted({item: { data: { extra_data, owner = null } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-check-square-o"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('survey_review_reverted')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('review_reverted_content', {name: I18n.t(extra_data['report_name'][I18n.locale])})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function SurveyApprovalReverted({item: { data: { extra_data, owner = null }, project_id, event_feed_id }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-check-square-o"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('survey_approval_reverted')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('approval_reverted_content', {
                        request_name: I18n.t(extra_data['report_name'][I18n.locale]),
                        request_url: project_report_edit_report_path({project_id: project_id, report_id: extra_data['monitoring_report_id'], project_report_id: event_feed_id})})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function SurveyDraftSubmissionDeleted({item: { data: { extra_data, owner = null } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-check-square-o"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('survey_draft_submission_deleted')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('draft_submission_deleted_content', {
                        items_count: extra_data['project_report_ids'].length,
                        report_name: I18n.t(extra_data['report_name'][I18n.locale])})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function ReportSubmission({item: { data: { extra_data, owner = null }, project_id, event_feed_id }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-bar-chart"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('report_submission')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('report_submission_content', {
                        request_name: I18n.t(extra_data['report_name'][I18n.locale]),
                        request_url: project_report_edit_report_path({project_id: project_id, report_id: extra_data['monitoring_report_id'], project_report_id: event_feed_id})})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function ReportReviewCompleted({item: { data: { extra_data, owner = null } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-bar-chart"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('report_review_completed')}</h4>
            <div className="mb-1">
                {I18n.t(extra_data['decision'])} <span>{I18n.t(extra_data['report_name'][I18n.locale])}</span>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function ReportDecisionGiven({item: { data: { extra_data, owner = null } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-bar-chart"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('report_decision_given')}</h4>
            <div className="mb-1">
                {I18n.t(extra_data['decision'])} <span>{I18n.t(extra_data['report_name'][I18n.locale])}</span>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function ReportReviewReverted({item: { data: { extra_data, owner = null } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-bar-chart"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('report_review_reverted')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('review_reverted_content', {name: I18n.t(extra_data['report_name'][I18n.locale])})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function ReportApprovalReverted({item: { data: { extra_data, owner = null }, project_id, event_feed_id }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-bar-chart"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('report_approval_reverted')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('approval_reverted_content', {
                        request_name: I18n.t(extra_data['report_name'][I18n.locale]),
                        request_url: project_report_edit_report_path({project_id: project_id, report_id: extra_data['monitoring_report_id'], project_report_id: event_feed_id})})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function ReportDraftDeleted({item: { data: { extra_data, owner = null } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-bar-chart"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('report_draft_deleted')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('draft_submission_deleted_content', {
                        items_count: extra_data['project_report_ids'].length,
                        report_name: I18n.t(extra_data['report_name'][I18n.locale])})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function ReportRequestSubmission({item: { data: { extra_data, owner = null } }, children, showProfilePics}) {
    const MAX_ITEMS = 4;
    const [isOpen, setIsOpen] = useState(false);

    const getItems = () => {
        if (isOpen)
            return extra_data['project_ids'];
        return extra_data['project_ids'].slice(0, MAX_ITEMS);
    };

    const projectLinks = <div className="mb-1">
        {getItems()
            .map((id, i) => <a key={id} href={project_show_path({ note_id: id })} target='_blank'>{extra_data['project_identifiers'][i]}</a>)
            .reduce((prev, curr) => [prev, ' | ', curr])}

        {extra_data['project_ids'].length > MAX_ITEMS && <button
            onClick={() => setIsOpen((preV) => !preV)}
            className="btn btn-primary btn-xs ml-1">
            {I18n.t(isOpen ? 'less' : 'display_all')}
        </button>
        }
    </div>;

    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-bar-chart"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('report_request_for_submission')}</h4>
            <div dangerouslySetInnerHTML={
                {__html: I18n.t('request_for_submission_content', {report_name: I18n.t(extra_data['report_name'][I18n.locale])})}}>
            </div>
            { projectLinks }
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function DisbursementSubmission({item: { data: { extra_data, owner = null }, project_id, event_feed_id }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-check-square"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('disbursement_request_submission')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('disbursement_request_submission_content', {
                        request_name: I18n.t(extra_data['report_name'][I18n.locale]),
                        request_url: project_report_edit_report_path({project_id: project_id, report_id: extra_data['monitoring_report_id'], project_report_id: event_feed_id})})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function DisbursementReviewCompleted({item: { data: { extra_data, owner = null } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-check-square"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('disbursement_request_review_completed')}</h4>
            <div className="mb-1">
                {I18n.t(extra_data['decision'])} <span>{I18n.t(extra_data['report_name'][I18n.locale])}</span>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function DisbursementDecisionGiven({item: { data: { extra_data, owner = null } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-check-square"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('disbursement_request_decision_given')}</h4>
            <div className="mb-1">
                {I18n.t(extra_data['decision'])} <span>{I18n.t(extra_data['report_name'][I18n.locale])}</span>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function DisbursementReviewReverted({item: { data: { extra_data, owner = null } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-check-square"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('disbursement_request_review_reverted')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('review_reverted_content', {name: I18n.t(extra_data['report_name'][I18n.locale])})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function DisbursementApprovalReverted({item: { data: { extra_data, owner = null }, project_id, event_feed_id }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-check-square"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('disbursement_request_approval_reverted')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('approval_reverted_content', {
                        request_name: I18n.t(extra_data['report_name'][I18n.locale]),
                        request_url: project_report_edit_report_path({project_id: project_id, report_id: extra_data['monitoring_report_id'], project_report_id: event_feed_id})})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function DisbursementRequestDraftDeleted({item: { data: { extra_data, owner = null } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-check-square"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('disbursement_request_draft_deleted')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('draft_submission_deleted_content', {
                        items_count: extra_data['project_report_ids'].length,
                        report_name: I18n.t(extra_data['report_name'][I18n.locale])})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function DisbursementRequestSubmission({item: { data: { extra_data, owner = null } }, children, showProfilePics}) {
    const MAX_ITEMS = 4;
    const [isOpen, setIsOpen] = useState(false);

    const getItems = () => {
        if (isOpen)
            return extra_data['project_ids'];
        return extra_data['project_ids'].slice(0, MAX_ITEMS);
    };

    const projectLinks = <div className="mb-1">
        {getItems()
            .map((id, i) => <a key={id} href={project_show_path({ note_id: id })} target='_blank'>{extra_data['project_identifiers'][i]}</a>)
            .reduce((prev, curr) => [prev, ' | ', curr])}

        {extra_data['project_ids'].length > MAX_ITEMS && <button
            onClick={() => setIsOpen((preV) => !preV)}
            className="btn btn-primary btn-xs ml-1">
            {I18n.t(isOpen ? 'less' : 'display_all')}
        </button>
        }
    </div>;

    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-check-square"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('disbursement_request_for_submission')}</h4>
            <div dangerouslySetInnerHTML={
                {__html: I18n.t('request_for_submission_content', {report_name: I18n.t(extra_data['report_name'][I18n.locale])})}}>
            </div>
            { projectLinks }
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function DeadlineExtensionPeriod({item: { data: { project, extra_data, owner } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-clock-o"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('deadline_extension_period')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('deadline_extension_period_content', {
                        project_identifier: project['project_identifier'],
                        call_phase_name: I18n.t(extra_data['call_phase_name'])})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function DeadlineChanged({item: { data: { project, extra_data, owner } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-clock-o"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('deadline_changed')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('deadline_changed_content', {
                        full_name: `${owner['first_name']} ${owner['last_name']}`,
                        project_identifier: project['project_identifier'],
                        report_name: I18n.t(extra_data['report_name'][I18n.locale]),
                        phase_name: extra_data['phase_name'] ? `(${I18n.t(extra_data['phase_name'])})` : ''})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function SupportRequestReceived({item: { data: { extra_data, owner }, project_id }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="adalicon icon-support"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('support_request_received')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('support_request_received_content', {
                        request_name: extra_data['identifier'],
                        request_url: support_form_list_requests_path({project_id: project_id, identifier: extra_data['identifier']})})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function SupportRequestAnswered({item: { data: { extra_data, owner }, project_id }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="adalicon icon-support"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('support_request_answered')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('support_request_answered_content', {
                        request_name: extra_data['identifier'],
                        request_url: support_form_list_requests_path({project_id: project_id, identifier: extra_data['identifier']})})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function CrossTenantRequestReceived({item: { data: { extra_data, owner } }, children, showProfilePics}) {
    return (<>
        <div className="media-left">
            <a className="icon avatar avatar-on" href="#">
                <i className="fa fa-bar-chart"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading">{I18n.t('cross_tenant_request_received')}</h4>
            <div className="mb-1" dangerouslySetInnerHTML={
                {__html: I18n.t('cross_tenant_request_received_content', {
                        report_name: I18n.t(extra_data['report_name'][I18n.locale])})}}>
            </div>
            <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
        </div>
    </>)
}

function LiveDiscussionReplied({item : {data: { extra_data, owner} }, children}) {
    return (<>
        <div className="media-left">
            <a className={`icon avatar avatar-on icon-${extra_data.target_type}`} href="#">
                <i className="fa fa-comments"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading" dangerouslySetInnerHTML={
                {__html:I18n.t('new_comment_on', {type: I18n.t(extra_data.target_type) ,identifier: extra_data.identifier, link: extra_data.discussion_link})}}></h4>
            <div className="mb-1" style={{whiteSpace: 'pre-wrap'}}
            dangerouslySetInnerHTML={{ __html: extra_data.message }}
            />
            <a href={extra_data.discussion_link} target="_blank" rel="noopener noreferrer">{I18n.t('view_discussion')}</a>
            <OwnerEvent { ...{owner} }/>
        </div>
    </>)
}

function LiveDiscussionInitiated({item : {data: { extra_data, owner} }, children}) {
    return (<>
        <div className="media-left">
            <a className={`icon avatar avatar-on icon-${extra_data.target_type}`} href="#">
                <i className="fa fa-comments"/>
            </a>
        </div>
        <div className="media-body">
            {children}
            <h4 className="media-heading" dangerouslySetInnerHTML={
                {__html:I18n.t('new_live_discussion', {type: I18n.t(extra_data.target_type), identifier: extra_data.identifier, link: extra_data.discussion_link})}}></h4>
            <div className="mb-1">
                {/* live_discussion_topic */}
            </div>
            <a href={extra_data.discussion_link} target="_blank" rel="noopener noreferrer">{I18n.t('view_discussion')}</a>
            <OwnerEvent { ...{owner} }/>
        </div>
    </>)
}


function TemplatedEventContent({item: { event_feed_type, data: { extra_data = {}, owner = null }, rendered_title, rendered_message }, children, showProfilePics}) {
  const wrapperClass = (EVENT_TYPE_ICON_WRAPPER_CLASS[event_feed_type]?.(extra_data)) || '';
  return (<>
    <div className="media-left">
      <a className={`icon avatar avatar-on ${wrapperClass}`} href="#">
        <i className={EVENT_TYPE_ICONS[event_feed_type] || 'fa fa-bell-o'}/>
      </a>
    </div>
    <div className="media-body">
      {children}
      <h4 className="media-heading" dangerouslySetInnerHTML={{__html: rendered_title}}/>
      <div className="mb-1" dangerouslySetInnerHTML={{__html: decodeHTML(rendered_message)}}/>
      <OwnerEvent owner={owner} showProfilePics={showProfilePics}/>
    </div>
  </>);
}

export default function Advanced({item, showProfilePics, locale}) {
    const LegacyComponent = {
        application: {
            on_submitted: ApplicationSubmitted,
            on_review_completed: ApplicationReviewCompleted,
            on_decision_given: ApplicationDecisionGiven,
            on_decisions_published: ApplicationDecisionsPublished
        },
        survey: {
            on_submission: SurveySubmission,
            on_review_completed: SurveyReviewCompleted,
            on_decision_given: SurveyDecisionGiven,
            on_review_reverted: SurveyReviewReverted,
            on_approval_reverted: SurveyApprovalReverted,
            on_deleted: SurveyDraftSubmissionDeleted
        },
        report: {
            on_submission: ReportSubmission,
            on_review_completed: ReportReviewCompleted,
            on_decision_given: ReportDecisionGiven,
            on_review_reverted: ReportReviewReverted,
            on_approval_reverted: ReportApprovalReverted,
            on_deleted: ReportDraftDeleted,
            on_request_submission: ReportRequestSubmission
        },
        disbursement: {
            on_submission: DisbursementSubmission,
            on_review_completed: DisbursementReviewCompleted,
            on_decision_given: DisbursementDecisionGiven,
            on_review_reverted: DisbursementReviewReverted,
            on_approval_reverted: DisbursementApprovalReverted,
            on_deleted: DisbursementRequestDraftDeleted,
            on_request_submission: DisbursementRequestSubmission
        },
        deadline: {
            on_extension_period: DeadlineExtensionPeriod,
            on_changed: DeadlineChanged,
        },
        support: {
            on_support_request_received: SupportRequestReceived,
            on_support_request_answered: SupportRequestAnswered
        },
        cross_fund_report: {
            on_request_submission: CrossTenantRequestReceived,
        },
        programme_live_discussion: {
            on_reply: LiveDiscussionReplied,
            on_init: LiveDiscussionInitiated
        },
        project_live_discussion: {
            on_reply: LiveDiscussionReplied,
            on_init: LiveDiscussionInitiated,
            on_submission_reply: LiveDiscussionReplied
        },
        match_basket: {
            on_new_match: NewMatchBasketMatch
        },
    }[item.event_feed_type]?.[item.key];
    const Component = item.templated ? TemplatedEventContent : LegacyComponent;

    // Defaults to the page's active locale (same global used elsewhere in this file, e.g. for
    // report_name translation above) so the real event feed shows relative time in the viewer's
    // own language. `locale` can still be overridden explicitly - the admin template preview
    // renders every configured locale side by side and needs each one independently (see
    // EventFeedContentTemplatePreview.jsx). dayjs's .locale(xx) returns a new instance rather
    // than mutating in place, so each panel's relative-time text is independent by construction.
    const createdAtDayjs = dayjs.utc(item.created_at).local().startOf('second').locale(locale || I18n.locale);
    const created_at = createdAtDayjs.fromNow();

    return <div className="media event-feed-media">
        <Component item={item} showProfilePics={showProfilePics}>
            <small className="text-muted pull-right">{created_at}</small>
        </Component>
    </div>;
}
